/**
 * Settings Page - AI config, Category/Tag management, Trash management
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Category, type Tag, type Receipt } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

type SettingsTab = 'ai' | 'categories' | 'security' | 'trash';

const validTabs: SettingsTab[] = ['ai', 'categories', 'security', 'trash'];

export function SettingsPage() {
  const { isGlobalAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  // Org admins can't see AI tab, default to categories
  const defaultTab = isGlobalAdmin ? 'ai' : 'categories';
  const initialTab = tabParam && validTabs.includes(tabParam)
    ? (tabParam === 'ai' && !isGlobalAdmin ? 'categories' : tabParam)
    : defaultTab;
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // AI Settings
  const [aiProvider, setAiProvider] = useState('gemini');
  const [geminiModel, setGeminiModel] = useState('gemini-2.0-flash');
  const [openrouterModel, setOpenrouterModel] = useState('');
  const [vertexModel, setVertexModel] = useState('gemini-2.5-flash-lite');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [openrouterApiKey, setOpenrouterApiKey] = useState('');
  const [vertexApiKey, setVertexApiKey] = useState('');
  const [isSavingAI, setIsSavingAI] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  const [aiSaveMessage, setAiSaveMessage] = useState('');
  const [aiErrorMessage, setAiErrorMessage] = useState('');

  // Categories & Tags
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [isLoadingCatsTags, setIsLoadingCatsTags] = useState(true);

  // Edit state
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');

  // Trash
  const [trashedReceipts, setTrashedReceipts] = useState<Receipt[]>([]);
  const [isLoadingTrash, setIsLoadingTrash] = useState(true);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Load AI settings
  useEffect(() => {
    const loadAISettings = async () => {
      setIsLoadingAI(true);
      try {
        const result = await api.getAISettings();
        if (result.success && result.data) {
          setAiProvider(result.data.provider || 'gemini');
          setGeminiModel(result.data.geminiModel || 'gemini-2.0-flash');
          setOpenrouterModel(result.data.openrouterModel || '');
          setVertexModel(result.data.vertexModel || 'gemini-2.5-flash-lite');
          setGeminiApiKey(result.data.geminiApiKey || '');
          setOpenrouterApiKey(result.data.openrouterApiKey || '');
          setVertexApiKey(result.data.vertexApiKey || '');
        }
      } catch (error) {
        console.error('Failed to load AI settings:', error);
      } finally {
        setIsLoadingAI(false);
      }
    };
    loadAISettings();
  }, []);

  // Load categories and tags
  useEffect(() => {
    const loadCatsTags = async () => {
      setIsLoadingCatsTags(true);
      try {
        const [catResult, tagResult] = await Promise.all([
          api.getCategories(),
          api.getTags(),
        ]);
        if (catResult.success && catResult.data) {
          setCategories(catResult.data.categories);
        }
        if (tagResult.success && tagResult.data) {
          setTags(tagResult.data.tags);
        }
      } catch (error) {
        console.error('Failed to load categories/tags:', error);
      } finally {
        setIsLoadingCatsTags(false);
      }
    };
    loadCatsTags();
  }, []);

  // Load trash
  useEffect(() => {
    if (activeTab === 'trash') {
      loadTrash();
    }
  }, [activeTab]);

  const loadTrash = async () => {
    setIsLoadingTrash(true);
    try {
      // Note: We'd need an API endpoint for deleted receipts
      // For now, just show empty state
      setTrashedReceipts([]);
    } catch (error) {
      console.error('Failed to load trash:', error);
    } finally {
      setIsLoadingTrash(false);
    }
  };

  const handleSaveAISettings = async () => {
    setIsSavingAI(true);
    setAiSaveMessage('');
    setAiErrorMessage('');

    try {
      const result = await api.updateAISettings({
        provider: aiProvider,
        geminiModel: geminiModel,
        openrouterModel: openrouterModel,
        vertexModel: vertexModel,
        geminiApiKey: geminiApiKey,
        openrouterApiKey: openrouterApiKey,
        vertexApiKey: vertexApiKey,
      });

      if (result.success && result.data) {
        // Update state with returned values (API keys will be masked)
        setGeminiApiKey(result.data.geminiApiKey || '');
        setOpenrouterApiKey(result.data.openrouterApiKey || '');
        setVertexApiKey(result.data.vertexApiKey || '');
        setAiSaveMessage('Settings saved successfully!');
        setTimeout(() => setAiSaveMessage(''), 3000);
      } else {
        setAiErrorMessage(result.error?.message || 'Failed to save settings');
        setTimeout(() => setAiErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      setAiErrorMessage('Failed to save settings. Please try again.');
      setTimeout(() => setAiErrorMessage(''), 5000);
    } finally {
      setIsSavingAI(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const result = await api.createCategory(newCategoryName.trim());
      if (result.success && result.data) {
        setCategories([...categories, result.data]);
        setNewCategoryName('');
      } else {
        alert(result.error?.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      alert('Failed to create category');
    }
  };

  const handleEditCategory = (cat: Category) => {
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.name);
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const handleSaveCategory = async (id: string) => {
    if (!editingCategoryName.trim()) return;

    try {
      const result = await api.updateCategory(id, editingCategoryName.trim());
      if (result.success && result.data) {
        setCategories(categories.map((c) => (c.id === id ? result.data! : c)));
        setEditingCategoryId(null);
        setEditingCategoryName('');
      } else {
        alert(result.error?.message || 'Failed to update category');
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      const result = await api.deleteCategory(id);
      if (result.success) {
        setCategories(categories.filter((c) => c.id !== id));
      } else {
        alert(result.error?.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const result = await api.createTag(newTagName.trim());
      if (result.success && result.data) {
        setTags([...tags, result.data]);
        setNewTagName('');
      } else {
        alert(result.error?.message || 'Failed to create tag');
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
      alert('Failed to create tag');
    }
  };

  const handleEditTag = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
  };

  const handleCancelEditTag = () => {
    setEditingTagId(null);
    setEditingTagName('');
  };

  const handleSaveTag = async (id: string) => {
    if (!editingTagName.trim()) return;

    try {
      const result = await api.updateTag(id, editingTagName.trim());
      if (result.success && result.data) {
        setTags(tags.map((t) => (t.id === id ? result.data! : t)));
        setEditingTagId(null);
        setEditingTagName('');
      } else {
        alert(result.error?.message || 'Failed to update tag');
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      alert('Failed to update tag');
    }
  };

  const handleDeleteTag = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tag?')) return;

    try {
      const result = await api.deleteTag(id);
      if (result.success) {
        setTags(tags.filter((t) => t.id !== id));
      } else {
        alert(result.error?.message || 'Failed to delete tag');
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      alert('Failed to delete tag');
    }
  };

  const handleEmptyTrash = async () => {
    if (!confirm('Are you sure you want to permanently delete all items in trash?')) return;
    setTrashedReceipts([]);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      const result = await api.changePassword(currentPassword, newPassword);

      if (result.success) {
        setPasswordMessage('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordMessage(''), 3000);
      } else {
        setPasswordError(result.error?.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-8">Settings</h1>

      {/* Tabs - Horizontally scrollable on mobile */}
      <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
        {[
          // AI Config only visible to global admins
          ...(isGlobalAdmin ? [{ id: 'ai' as SettingsTab, label: 'AI Config', fullLabel: 'AI Configuration' }] : []),
          { id: 'categories' as SettingsTab, label: 'Categories', fullLabel: 'Categories & Tags' },
          { id: 'security' as SettingsTab, label: 'Security', fullLabel: 'Security' },
          { id: 'trash' as SettingsTab, label: 'Trash', fullLabel: 'Trash' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="sm:hidden">{tab.label}</span>
            <span className="hidden sm:inline">{tab.fullLabel}</span>
          </button>
        ))}
      </div>

      {/* AI Settings Tab - Global Admin Only */}
      {activeTab === 'ai' && isGlobalAdmin && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">AI Configuration</h2>

          {isLoadingAI ? (
            <div className="text-center text-gray-500 py-6 sm:py-8">Loading AI settings...</div>
          ) : (
            <div className="space-y-4 sm:space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Provider
                </label>
                <select
                  value={aiProvider}
                  onChange={(e) => setAiProvider(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="gemini">Google Gemini</option>
                  <option value="openrouter">OpenRouter</option>
                  <option value="vertex">Google Vertex AI</option>
                </select>
              </div>

              {aiProvider === 'gemini' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gemini Model
                    </label>
                    <input
                      type="text"
                      value={geminiModel}
                      onChange={(e) => setGeminiModel(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="gemini-2.0-flash"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gemini API Key
                    </label>
                    <input
                      type="password"
                      value={geminiApiKey}
                      onChange={(e) => setGeminiApiKey(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Enter your Gemini API key"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {geminiApiKey && geminiApiKey.includes('***')
                        ? 'API key is saved (masked for security). Enter a new key to update.'
                        : 'Enter your Google AI Studio API key'}
                    </p>
                  </div>
                </>
              )}

              {aiProvider === 'openrouter' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OpenRouter Model
                    </label>
                    <input
                      type="text"
                      value={openrouterModel}
                      onChange={(e) => setOpenrouterModel(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="anthropic/claude-3-haiku"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OpenRouter API Key
                    </label>
                    <input
                      type="password"
                      value={openrouterApiKey}
                      onChange={(e) => setOpenrouterApiKey(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Enter your OpenRouter API key"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {openrouterApiKey && openrouterApiKey.includes('***')
                        ? 'API key is saved (masked for security). Enter a new key to update.'
                        : 'Enter your OpenRouter API key'}
                    </p>
                  </div>
                </>
              )}

              {aiProvider === 'vertex' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vertex AI Model
                    </label>
                    <input
                      type="text"
                      value={vertexModel}
                      onChange={(e) => setVertexModel(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="gemini-2.5-flash-lite"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vertex AI API Key
                    </label>
                    <input
                      type="password"
                      value={vertexApiKey}
                      onChange={(e) => setVertexApiKey(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Enter your Vertex AI API key"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {vertexApiKey && vertexApiKey.includes('***')
                        ? 'API key is saved (masked for security). Enter a new key to update.'
                        : 'Enter your Vertex AI Express mode API key'}
                    </p>
                  </div>
                </>
              )}

              {aiSaveMessage && (
                <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm">
                  {aiSaveMessage}
                </div>
              )}

              {aiErrorMessage && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {aiErrorMessage}
                </div>
              )}

              <button
                onClick={handleSaveAISettings}
                disabled={isSavingAI}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isSavingAI ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Categories & Tags Tab */}
      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Categories */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Categories</h2>

            <div className="flex gap-2 mb-3 sm:mb-4">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm sm:text-base"
                placeholder="New category name"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <button
                onClick={handleAddCategory}
                className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
              >
                Add
              </button>
            </div>

            {isLoadingCatsTags ? (
              <div className="text-center text-gray-500 py-4">Loading...</div>
            ) : categories.length === 0 ? (
              <div className="text-center text-gray-500 py-4">No categories</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {categories.map((cat) => (
                  <li key={cat.id} className="py-3 flex items-center justify-between gap-2">
                    {editingCategoryId === cat.id ? (
                      <>
                        <input
                          type="text"
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCategory(cat.id);
                            if (e.key === 'Escape') handleCancelEditCategory();
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveCategory(cat.id)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEditCategory}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-gray-900 flex-1">{cat.name}</span>
                        <button
                          onClick={() => handleEditCategory(cat)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Tags */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Tags</h2>

            <div className="flex gap-2 mb-3 sm:mb-4">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm sm:text-base"
                placeholder="New tag name"
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
              />
              <button
                onClick={handleAddTag}
                className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
              >
                Add
              </button>
            </div>

            {isLoadingCatsTags ? (
              <div className="text-center text-gray-500 py-4">Loading...</div>
            ) : tags.length === 0 ? (
              <div className="text-center text-gray-500 py-4">No tags</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {tags.map((tag) => (
                  <li key={tag.id} className="py-3 flex items-center justify-between gap-2">
                    {editingTagId === tag.id ? (
                      <>
                        <input
                          type="text"
                          value={editingTagName}
                          onChange={(e) => setEditingTagName(e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTag(tag.id);
                            if (e.key === 'Escape') handleCancelEditTag();
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveTag(tag.id)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEditTag}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-gray-900 flex-1">{tag.name}</span>
                        <button
                          onClick={() => handleEditTag(tag)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Change Password</h2>

          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Confirm new password"
              />
            </div>

            {passwordError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {passwordError}
              </div>
            )}

            {passwordMessage && (
              <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm">
                {passwordMessage}
              </div>
            )}

            <button
              onClick={handleChangePassword}
              disabled={isChangingPassword}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}

      {/* Trash Tab */}
      {activeTab === 'trash' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Trash</h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Items are automatically deleted after 30 days
              </p>
            </div>
            {trashedReceipts.length > 0 && (
              <button
                onClick={handleEmptyTrash}
                className="text-red-600 hover:text-red-800 font-medium text-sm self-start sm:self-auto"
              >
                Empty Trash
              </button>
            )}
          </div>

          {isLoadingTrash ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">Loading...</div>
          ) : trashedReceipts.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <div className="text-3xl sm:text-4xl mb-2">🗑️</div>
              <div>Trash is empty</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Vendor
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {trashedReceipts.map((receipt) => (
                    <tr key={receipt.id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-gray-900">
                        {receipt.vendor || 'Unknown'}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-500 whitespace-nowrap">{receipt.date}</td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-right font-semibold whitespace-nowrap">
                        {receipt.currency} {receipt.total.toFixed(2)}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-right whitespace-nowrap">
                        <button className="text-blue-600 hover:text-blue-800 text-sm mr-3">
                          Restore
                        </button>
                        <button className="text-red-600 hover:text-red-800 text-sm">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
